/**
 * Dashboard Edit Store
 *
 * Zustand store for managing dashboard edit mode state.
 */
import { create } from 'zustand';
import type { DashboardSpec, ChartDefinition, ChartGridPosition, FilterDefinition } from '@solidafy/bi-core';

const GRID_COLS = 12;

/**
 * Find the next available position in the grid that can fit a widget of given size.
 * Scans left-to-right, top-to-bottom to fill horizontal gaps first.
 */
function findNextAvailablePosition(charts: ChartDefinition[], newW: number, newH: number): { x: number; y: number } {
  if (charts.length === 0) {
    return { x: 0, y: 0 };
  }

  // Calculate grid height needed
  const maxY = Math.max(0, ...charts.map((c) => (c.grid?.y ?? 0) + (c.grid?.h ?? 4)));
  const gridHeight = maxY + newH + 1;

  // Build occupancy grid
  const occupied: boolean[][] = Array.from({ length: gridHeight }, () => Array<boolean>(GRID_COLS).fill(false));

  // Mark occupied cells
  for (const chart of charts) {
    const { x = 0, y = 0, w = 6, h = 4 } = chart.grid || {};
    for (let row = y; row < y + h && row < gridHeight; row++) {
      for (let col = x; col < x + w && col < GRID_COLS; col++) {
        occupied[row][col] = true;
      }
    }
  }

  // Find first position where new widget fits (scan top-to-bottom, left-to-right)
  for (let y = 0; y < gridHeight; y++) {
    for (let x = 0; x <= GRID_COLS - newW; x++) {
      let fits = true;
      outer: for (let row = y; row < y + newH; row++) {
        for (let col = x; col < x + newW; col++) {
          if (occupied[row]?.[col]) {
            fits = false;
            break outer;
          }
        }
      }
      if (fits) {
        return { x, y };
      }
    }
  }

  // Fallback: place at bottom-left
  return { x: 0, y: maxY };
}

interface DashboardEditState {
  isEditing: boolean;
  originalSpec: DashboardSpec | null;
  editSpec: DashboardSpec | null;
  selectedChartId: string | null;
  isDirty: boolean;

  enterEditMode: (spec: DashboardSpec) => void;
  exitEditMode: () => void;
  markSaved: () => void;
  getEditSpec: () => DashboardSpec | null;

  selectChart: (id: string | null) => void;
  addChart: (chart: ChartDefinition) => void;
  updateChart: (id: string, updates: Partial<ChartDefinition>) => void;
  deleteChart: (id: string) => void;
  duplicateChart: (id: string) => void;

  addFilter: (filter: FilterDefinition) => void;
  updateFilter: (id: string, updates: Partial<FilterDefinition>) => void;
  deleteFilter: (id: string) => void;

  updateName: (name: string) => void;
  updateDescription: (description: string) => void;
  updateLayout: (layouts: { i: string; x: number; y: number; w: number; h: number }[]) => void;
}

export const useDashboardEditStore = create<DashboardEditState>((set, get) => ({
  isEditing: false,
  originalSpec: null,
  editSpec: null,
  selectedChartId: null,
  isDirty: false,

  enterEditMode: (spec) => {
    const clone = JSON.parse(JSON.stringify(spec)) as DashboardSpec;
    set({
      isEditing: true,
      originalSpec: spec,
      editSpec: clone,
      selectedChartId: null,
      isDirty: false,
    });
  },

  exitEditMode: () => {
    set({
      isEditing: false,
      originalSpec: null,
      editSpec: null,
      selectedChartId: null,
      isDirty: false,
    });
  },

  markSaved: () => {
    const spec = get().editSpec;
    if (spec) {
      set({
        originalSpec: JSON.parse(JSON.stringify(spec)) as DashboardSpec,
        isDirty: false,
      });
    }
  },

  getEditSpec: () => get().editSpec,

  selectChart: (id) => set({ selectedChartId: id }),

  addChart: (chart) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }

    // Calculate next available position if chart doesn't have explicit grid
    const w = chart.grid?.w ?? 6;
    const h = chart.grid?.h ?? 4;
    const pos = findNextAvailablePosition(spec.charts, w, h);
    const chartWithGrid: ChartDefinition = {
      ...chart,
      grid: chart.grid ? { ...chart.grid, ...pos } : { x: pos.x, y: pos.y, w, h },
    };

    const updated = { ...spec, charts: [...spec.charts, chartWithGrid] };
    set({ editSpec: updated, isDirty: true });
  },

  updateChart: (id, updates) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    const charts = spec.charts.map((c) => (c.id === id ? { ...c, ...updates } : c));
    set({ editSpec: { ...spec, charts }, isDirty: true });
  },

  deleteChart: (id) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    const charts = spec.charts.filter((c) => c.id !== id);
    const selectedChartId = get().selectedChartId === id ? null : get().selectedChartId;
    set({ editSpec: { ...spec, charts }, selectedChartId, isDirty: true });
  },

  duplicateChart: (id) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    const original = spec.charts.find((c) => c.id === id);
    if (!original) {
      return;
    }

    const w = original.grid?.w ?? 6;
    const h = original.grid?.h ?? 4;
    const pos = findNextAvailablePosition(spec.charts, w, h);

    const cloned = JSON.parse(JSON.stringify(original)) as ChartDefinition;
    const newChart: ChartDefinition = {
      ...cloned,
      id: crypto.randomUUID(),
      title: `${original.title || 'Widget'} (copy)`,
      grid: { x: pos.x, y: pos.y, w, h },
    };
    set({ editSpec: { ...spec, charts: [...spec.charts, newChart] }, isDirty: true });
  },

  addFilter: (filter) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    const filters = [...(spec.filters || []), filter];
    set({ editSpec: { ...spec, filters }, isDirty: true });
  },

  updateFilter: (id, updates) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    const filters = (spec.filters || []).map((f) => (f.id === id ? { ...f, ...updates } : f));
    set({ editSpec: { ...spec, filters }, isDirty: true });
  },

  deleteFilter: (id) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    const filters = (spec.filters || []).filter((f) => f.id !== id);
    set({ editSpec: { ...spec, filters }, isDirty: true });
  },

  updateName: (name) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    set({
      editSpec: { ...spec, dashboard: { ...spec.dashboard, title: name } },
      isDirty: true,
    });
  },

  updateDescription: (description) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    set({
      editSpec: { ...spec, dashboard: { ...spec.dashboard, description } },
      isDirty: true,
    });
  },

  updateLayout: (layouts) => {
    const spec = get().editSpec;
    if (!spec) {
      return;
    }
    let changed = false;
    const charts = spec.charts.map((c) => {
      const layout = layouts.find((l) => l.i === c.id);
      if (!layout) {
        return c;
      }
      const g = c.grid;
      if (g && g.x === layout.x && g.y === layout.y && g.w === layout.w && g.h === layout.h) {
        return c;
      }
      changed = true;
      const grid: ChartGridPosition = { x: layout.x, y: layout.y, w: layout.w, h: layout.h };
      return { ...c, grid };
    });
    if (!changed) {
      return;
    }
    set({ editSpec: { ...spec, charts }, isDirty: true });
  },
}));
